/**
 * Locked stadium cinematic timings — single source of truth.
 *
 * Ending (from GOAL):
 * Hold far aim cam until net → brief GOAL beat → Highlight REPLAY
 * → Bloom → Blur → Portfolio assemble
 * (No post-goal stadium pans / drone / jersey cam tours.)
 */

export type StadiumBeat =
  | 'enter'
  | 'enterStop'
  | 'silence'
  | 'crowdRoar'
  | 'reveal'
  | 'heroShot'
  | 'ballNotice'
  | 'stance'
  | 'breath'
  | 'keeperPrep'
  | 'crowdQuiet'
  | 'preAim'
  | 'aim'
  | 'lean'
  | 'plant'
  | 'backSwing'
  | 'forwardSwing'
  | 'contactFreeze'
  | 'flight'
  | 'goal'
  | 'droneHero'
  | 'jerseyReveal'
  | 'stadiumShutdown'
  | 'celeVideo'
  | 'whiteBloom'
  | 'blurAssemble'
  // legacy aliases kept for CameraDirector compatibility
  | 'celebrate'
  | 'lookAround'
  | 'welcome'
  | 'morph';

/** Tunnel exit (TunnelExitCinematic) */
export const EXIT_FREEZE_S = 0.7;
/** Kept for imports; live exit walk duration lives in TunnelExitCinematic. */
export const EXIT_STEP_S = 5.5;
export const EXIT_TOTAL_S = EXIT_FREEZE_S + EXIT_STEP_S;
/** Black covers exit VO + monologue only — hand off to shot as last line ends. */
export const EXIT_BLACK_VO_MS = 17400;
export const EXIT_BLACK_PAD_MS = 200;
/** Full black hold after overlay, before VO / monologue. */
export const EXIT_BLACKOUT_HOLD_MS = 900;
export const EXIT_BLACK_MS = EXIT_BLACK_VO_MS + EXIT_BLACK_PAD_MS;
export const EXIT_FAILSAFE_MS = 28000;

/** On-black monologue lines (synced loosely to the exit VO). */
export const EXIT_BLACK_LINES: { text: string; atMs: number; holdMs: number }[] =
  [
    { text: 'The code is written.', atMs: 400, holdMs: 2200 },
    { text: 'The vision is alive.', atMs: 2200, holdMs: 2400 },
    {
      text: 'Every decision... every challenge... every late night... led to this moment.',
      atMs: 4800,
      holdMs: 5600,
    },
    {
      text: "There's only one thing left to do.",
      atMs: 10800,
      holdMs: 2800,
    },
    {
      text: "Take the shot... and discover what I've built",
      atMs: 13800,
      holdMs: 3600,
    },
  ];

export const EXPOSURE_TUNNEL = 0.68;
export const EXPOSURE_OUTSIDE = 0.92;
export const EXPOSURE_RAMP_S = 0.6;

export const CROWD_TUNNEL_START = 0.08;
export const CROWD_TUNNEL = 0.28;
/** Around the 5th skill card — stadium starts to bloom. */
export const CROWD_MID = 0.5;
export const CROWD_NEAR_EXIT = 0.68;
export const CROWD_MOUTH = 0.86;
export const CROWD_OUTSIDE = 1.0;

/**
 * Pre-aim spine — monologue black already finished; ShotAim opens on the pitch (no second black).
 */
export const ENTER_MS = 0;
export const ENTER_STOP_MS = 0;
export const SILENCE_MS = 0;
export const CROWD_ROAR_MS = 0;
export const REVEAL_MS = 480;
export const FLOOD_BURST_MS = 280;
export const HERO_SHOT_MS = 160;
export const BALL_NOTICE_MS = 140;
export const STANCE_MS = 110;
export const BREATH_MS = 90;
export const KEEPER_PREP_MS = 120;
export const CROWD_QUIET_MS = 90;
export const PRE_AIM_MS = 160;
export const MISSION_HUD_MS = 0;
/**
 * After exit VO: How-to-Play briefing covers load. Global black only clears
 * into that briefing (pitch stays hidden until briefing dismisses).
 */
export const SHOT_BLACK_HOLD_MS = 0;
export const SHOT_BLACK_FADE_MS = 280;
/** Min time to read how-to-play while the pitch warms. */
export const SHOT_BRIEFING_MIN_MS = 2800;
/** Failsafe — never leave Continue locked if a warm signal stalls. */
export const SHOT_BRIEFING_MAX_MS = 5000;
/** Soft unlock dive gate if GLBs are slow (OneDrive / first parse). */
export const SHOT_DIVES_FAILSAFE_MS = 1500;
/** Frames the aim camera must draw before we call the pitch "live". */
export const SHOT_WARM_FRAMES = 12;

function cum(...parts: number[]) {
  return parts.reduce((a, b) => a + b, 0);
}

export const AT = {
  enterStop: ENTER_MS,
  silence: cum(ENTER_MS, ENTER_STOP_MS),
  crowdRoar: cum(ENTER_MS, ENTER_STOP_MS, SILENCE_MS),
  reveal: cum(ENTER_MS, ENTER_STOP_MS, SILENCE_MS, CROWD_ROAR_MS),
  heroShot: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
  ),
  ballNotice: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
  ),
  stance: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
    BALL_NOTICE_MS,
  ),
  breath: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
    BALL_NOTICE_MS,
    STANCE_MS,
  ),
  keeperPrep: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
    BALL_NOTICE_MS,
    STANCE_MS,
    BREATH_MS,
  ),
  crowdQuiet: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
    BALL_NOTICE_MS,
    STANCE_MS,
    BREATH_MS,
    KEEPER_PREP_MS,
  ),
  preAim: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
    BALL_NOTICE_MS,
    STANCE_MS,
    BREATH_MS,
    KEEPER_PREP_MS,
    CROWD_QUIET_MS,
  ),
  aim: cum(
    ENTER_MS,
    ENTER_STOP_MS,
    SILENCE_MS,
    CROWD_ROAR_MS,
    REVEAL_MS,
    HERO_SHOT_MS,
    BALL_NOTICE_MS,
    STANCE_MS,
    BREATH_MS,
    KEEPER_PREP_MS,
    CROWD_QUIET_MS,
    PRE_AIM_MS,
  ),
} as const;

/**
 * Kick staging — Mixamo strike plays cleanly from frame 0;
 * ball leaves at authored foot-contact (~520ms @ timeScale 1).
 */
export const KICK_CONTACT_MS = 520;
export const KICK_LEAN_MS = 80;
export const KICK_PLANT_MS = 120;
export const KICK_BACK_MS = 160;
/** Keeper slide begins mid wind-up */
export const KICK_FORWARD_AT_MS = 280;
export const KICK_FREEZE_MS = 40;

export const KICK_AT = {
  plant: KICK_LEAN_MS,
  backSwing: KICK_LEAN_MS + KICK_PLANT_MS,
  forwardSwing: KICK_FORWARD_AT_MS,
  contactFreeze: Math.max(
    KICK_FORWARD_AT_MS + 60,
    KICK_CONTACT_MS - KICK_FREEZE_MS,
  ),
  flight: KICK_CONTACT_MS,
} as const;

/** —— ENDING (ms from GOAL) —— */
export const GOAL_NET_MS = 0;
export const GOAL_KEEPER_MS = 350; // compat
export const GOAL_CROWD_MS = 700; // compat
export const GOAL_FLASH_MS = 1200;
export const GOAL_PLAYER_MS = 1400; // compat
export const GOAL_SKY_MS = 1800; // compat

/** Hold aim cam on GOAL with keeper on the ground — then cut to celebration video */
export const END_GOAL_HOLD_MS = 2400;
/** @deprecated — dance removed */
export const END_DANCE_MS = END_GOAL_HOLD_MS;
export const END_DANCE_DUR_MS = 0;
/** @deprecated — stadium jersey cam removed */
export const END_DRONE_MS = END_GOAL_HOLD_MS;
export const END_JERSEY_MS = END_GOAL_HOLD_MS;
export const END_JERSEY_SWAP_MS = 800;
export const END_LOGO_MS = 300;
/** Blackout after short goal hold, then trophy celebration video */
export const END_SHUTDOWN_MS = END_GOAL_HOLD_MS;
/** Start celebration reel shortly after black */
export const END_VIDEO_MS = END_SHUTDOWN_MS + 280;
/** Max wait for celebration video before forcing leave (ms) */
export const END_VIDEO_DUR_MS = 22000;
/** white bloom → blur → portfolio (offsets from video end are handled in ShotAim) */
export const END_BLOOM_MS = 700;
export const END_BLUR_MS = 1500;
export const END_ASSEMBLE_MS = 2400;
export const END_LEAVE_MS = 3200;

/** @deprecated aliases — ending no longer uses these as primary */
export const GOAL_CELEBRATE_MS = END_DRONE_MS;
export const GOAL_LOOKAROUND_MS = END_JERSEY_MS;
export const GOAL_WELCOME_MS = END_JERSEY_MS;
export const GOAL_MORPH_MS = END_SHUTDOWN_MS;
export const GOAL_LEAVE_MS = END_LEAVE_MS;

export const CAM_EASE_FAST = 0.4;
export const CAM_EASE_MED = 0.6;
export const CAM_EASE_SLOW = 0.8;
/** Broadcast handheld — ~2–3cm vertical + tiny sway */
export const CAM_BREATH_Y = 0.028;
export const CAM_BREATH_PITCH = (0.35 * Math.PI) / 180;
export const CAM_BREATH_X = 0.018;

export const DESTINY_START_Z = 5.8;
export const GOAL_LINE_Z = -12.4;
/**
 * Keeper stands clear of the mouth (toward the spot), not on the goal line.
 * ~2.1m gap so posts/net read behind him.
 */
export const KEEPER_LINE_Z = -10.3;
/** FIFA: 11m from goal line — ball sits here at the player's feet. */
export const PENALTY_SPOT_Z = -1.4;
/** Player plants just behind the ball so the kick starts at the feet. */
export const DESTINY_END_Z = PENALTY_SPOT_Z + 0.38;
export const KICK_SHAKE = 0.018;

/** Jersey reveal display name (short, premium) */
export const JERSEY_REAL_NAME = 'ADARSH';
/** Trophy celebration highlight — desktop / mobile variants after GOAL. */
export const CELEBRATION_VIDEO_DESKTOP_URL =
  '/video/trophy-celebration-desktop.mp4';
export const CELEBRATION_VIDEO_MOBILE_URL =
  '/video/trophy-celebration-mobile.mp4';
/** @deprecated Prefer getCelebrationVideoUrl() */
export const CELEBRATION_VIDEO_URL = CELEBRATION_VIDEO_DESKTOP_URL;

export function getCelebrationVideoUrl(mobile = false) {
  return mobile
    ? CELEBRATION_VIDEO_MOBILE_URL
    : CELEBRATION_VIDEO_DESKTOP_URL;
}
